import { tallyTokens } from "../../theme.tsx";
import { useDispatch, useSelector } from "react-redux";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import * as React from "react";
import { Box } from "@mui/material";
import {
  deleteConversation,
  fetchExchanges,
  patchConversation,
  conversationActions,
  selectConversationAction,
} from "../../store/conversation.tsx";
import { Conversation } from "../../store/types.tsx";
import ConversationActivityDot from "./ConversationActivityDot.tsx";
import RowMenu from "./RowMenu.tsx";
import {
  PinActionIcon,
  UnpinActionIcon,
  RenameActionIcon,
  DeleteActionIcon,
} from "../icons/DrawerNavIcons.tsx";
import { PinnedMarkIcon } from "../icons/DrawerNavIcons.tsx";
import TallyDialog from "../dialog/TallyDialog.tsx";
import { i18n } from "@lingui/core";
import { msg, t } from "@lingui/core/macro";
import { useNavigate } from "react-router-dom";
import { setConversationUrl } from "./conversationUrl.ts";

interface ConversationDrawerSectionProps {
  conversations: Conversation[];
  title: string;
  toggleConversation: boolean;
  closeDrawer: () => void;
  onConversationSelected?: () => void;
}

interface PartialConversation {
  id: string;
  title: string | null;
}

interface StoreState {
  conversationState: {
    selectedConversation: string;
    selectedView: "conversation" | "dashboard" | "notifications";
    conversationActivityById: Record<string, { status: "working" | "ready" }>;
  };
}

const ConversationDrawerSection: React.FC<ConversationDrawerSectionProps> = (
  props,
) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [editConversation, setEditConversation] =
    React.useState<PartialConversation | null>(null);
  const [isRenameDialogOpen, setRenameDialogOpen] =
    React.useState<boolean>(false);

  const handleRenameDialogOpen = (
    conversationId: string,
    conversationTitle: string | null,
  ) => {
    setEditConversation({ id: conversationId, title: conversationTitle });
    setRenameDialogOpen(true);
  };

  // Close the dialog
  const handleRenameDialogClose = () => {
    setRenameDialogOpen(false);
  };

  const handleRenameConversation = async (newConversationTitle: string) => {
    if (
      newConversationTitle !== undefined &&
      editConversation !== null &&
      newConversationTitle.trim() != ""
    ) {
      dispatch(
        patchConversation(
          editConversation.id,
          JSON.stringify({ title: newConversationTitle }),
        ) as any,
      );
      handleRenameDialogClose();
    } else {
    }
  };

  const { selectedConversation, selectedView, conversationActivityById } =
    useSelector((state: StoreState) => state.conversationState);

  const togglePinConversation = async (
    id: string,
    is_pinned_value: boolean,
  ) => {
    dispatch(
      patchConversation(
        id,
        JSON.stringify({ is_pinned: is_pinned_value }),
      ) as any,
    );
  };

  const selectConversation = async (id: string) => {
    props.closeDrawer();
    dispatch(conversationActions.setSelectedView("conversation"));
    dispatch(selectConversationAction(id) as any);
    dispatch(fetchExchanges(id) as any);
    props.onConversationSelected?.();
    setConversationUrl(navigate, id);
  };

  const deleteConversationAction = async (id: string) => {
    dispatch(deleteConversation(id) as any);
  };

  return (
    props.conversations.length > 0 && (
      <div className="conversation-drawer-section">
        <h4>{props.title}</h4>
        <List>
          {props.conversations.map((conversation) => {
            const activity = conversationActivityById[conversation.id];
            const conversationTitle = conversation.title || "";
            // Accessible name for a conversation row, carrying its activity
            // state so the dot's meaning reaches screen-reader users too.
            const rowLabel =
              activity?.status === "working"
                ? t`${conversationTitle} \u2014 answering`
                : activity?.status === "ready"
                  ? t`${conversationTitle} \u2014 answer ready`
                  : undefined;
            return (
              <ListItem
                key={conversation.id}
                disablePadding
                sx={{
                  borderRadius: "25px",
                  transition: "all 0.2s ease",
                  overflow: "visible",
                }}
              >
                <Box
                  className="conversation-section-full-width"
                  sx={[
                    {
                      borderRadius: `${tallyTokens.radius.sm}px`,
                      color: tallyTokens.ink2,
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      width: "100%",
                    },
                    {
                      "&:hover": {
                        backgroundColor: tallyTokens.surface3,
                      },
                    },
                    // Selected reads as a raised chip lifted off the drawer, which
                    // is how the approved design distinguishes it.
                    selectedView === "conversation" &&
                      selectedConversation === conversation.id && {
                        backgroundColor: tallyTokens.surface,
                        color: tallyTokens.ink,
                        fontWeight: 600,
                        borderRadius: `${tallyTokens.radius.sm}px`,
                        boxShadow: tallyTokens.shadow.sm,
                        "&:hover": {
                          backgroundColor: tallyTokens.surface,
                        },
                      },
                  ]}
                >
                  {/* Reserved on every row, whatever its state, so a title never
                    shifts sideways when an answer starts or finishes. */}
                  <Box
                    sx={{
                      width: "16px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    {activity && (
                      <ConversationActivityDot status={activity.status} />
                    )}
                  </Box>
                  {conversation.is_pinned && (
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        color: "inherit",
                        opacity: 0.75,
                        flexShrink: 0,
                      }}
                    >
                      <PinnedMarkIcon className="drawer-row-mark" />
                    </Box>
                  )}
                  <ListItemButton
                    disableRipple
                    onClick={() => selectConversation(conversation.id)}
                    aria-label={rowLabel}
                    sx={{
                      flex: 1,
                      borderRadius: "25px",
                      // MUI gives ListItemButton 16px side padding by default,
                      // which pushed titles out of line with the section headers.
                      // The wrapper already supplies the inset.
                      px: 0,
                      py: "5px",
                      minHeight: 0,
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                    }}
                  >
                    <ListItemText
                      primary={conversation.title}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        my: 0,
                        "& .MuiListItemText-primary": {
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          fontSize: "0.875rem",
                          lineHeight: 1.35,
                          color: "inherit",
                        },
                      }}
                    />
                  </ListItemButton>
                  {selectedView === "conversation" &&
                    selectedConversation === conversation.id && (
                      <RowMenu
                        ariaLabel={i18n._(
                          msg({
                            context:
                              "Accessible name for the conversation overflow menu",
                            message: "Conversation options",
                          }),
                        )}
                        actions={[
                          {
                            id: "pin",
                            icon: props.toggleConversation ? (
                              <PinActionIcon />
                            ) : (
                              <UnpinActionIcon />
                            ),
                            label: props.toggleConversation
                              ? i18n._(
                                  msg({
                                    context: "Button title of the pin button",
                                    message: "Pin",
                                  }),
                                )
                              : i18n._(
                                  msg({
                                    context: "Button title of the unpin button",
                                    message: "Unpin",
                                  }),
                                ),
                            onClick: () =>
                              togglePinConversation(
                                conversation.id,
                                props.toggleConversation,
                              ),
                          },
                          {
                            id: "rename",
                            icon: <RenameActionIcon />,
                            label: i18n._(
                              msg({
                                context: "Button title of the rename button.",
                                message: "Rename",
                              }),
                            ),
                            onClick: () =>
                              handleRenameDialogOpen(
                                conversation.id,
                                conversation.title,
                              ),
                          },
                          {
                            id: "delete",
                            icon: <DeleteActionIcon />,
                            label: i18n._(
                              msg({
                                context: "Button title of the delete button.",
                                message: "Delete",
                              }),
                            ),
                            destructive: true,
                            onClick: () =>
                              deleteConversationAction(conversation.id),
                          },
                        ]}
                      />
                    )}
                </Box>
              </ListItem>
            );
          })}
        </List>
        <TallyDialog
          open={isRenameDialogOpen}
          title={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename conversation",
            }),
          )}
          subtitle={i18n._(
            msg({
              context:
                "Span above the title input explaining the limitation of the field bellow.",
              message: "Field cannot be longer than 100 characters",
            }),
          )}
          submitTitle={i18n._(
            msg({
              context: "Button title of the rename conversation button",
              message: "Rename",
            }),
          )}
          onSubmit={handleRenameConversation}
          initialInputValue={editConversation?.title ?? ""}
          handleDialogClose={handleRenameDialogClose}
        />
      </div>
    )
  );
};

export default ConversationDrawerSection;
